<template>
  <v-card flat class="pa-5 bg-pwt_snow">
    <!-- selected vehicle card -->
    <DefaultVehicleCard @view-vehicle="emitViewVehicle" />
    <!-- saved vehicles side scroller list -->
    <v-card-text class="text-h6 px-0 px-md-4">
      <v-row no-gutters justify="space-between">
        <span> Saved vehicles </span>
        <TextBtn @click="handleAddVehicle"> Add vehicle </TextBtn>
      </v-row>
      <v-slide-group class="pt-2" center-active>
        <v-slide-group-item
          v-for="(vehicle, index) in customVehicles"
          :key="'saved-vehicle-' + index"
          class="pr-2"
          :class="index == 0 ? 'pl-1' : ''"
        >
          <DashboardVehicleListCard
            style="width: 300px"
            :vehicle="vehicle"
            class="ma-1"
            @click="emitViewVehicle(vehicle)"
          />
        </v-slide-group-item>
      </v-slide-group>
    </v-card-text>
    <!-- fleet vehicles two column list -->
    <v-card-text class="text-h6 px-0 px-md-4">
      <v-row no-gutters justify="space-between">
        <span> Fleet vehicles </span>
        <TextBtn @click="showFleetFilterDialog = true"> Filter </TextBtn>
        <GenericDialog
          :activator="showFleetFilterDialog"
          @close="showFleetFilterDialog = false"
          small
        >
          <v-card-title>Fleet vehicle filters</v-card-title>
          <v-card-text>
            <v-checkbox
              hide-details
              label="Show only vehicles with active connections"
              color="primary"
              v-model="filter_onlyActiveConnections"
            />
            <v-checkbox
              hide-details
              label="Order by state of charge"
              color="primary"
              v-model="filter_orderByStateOfCharge"
            />
          </v-card-text>
        </GenericDialog>
      </v-row>
      <v-text-field
        v-if="unfilteredData.length"
        v-model="searchQuery"
        bg-color="background"
        clearable
        placeholder="Search fleet for vehicles available to you..."
        class="mt-4"
      />
      <v-row v-if="connectedVehicles.length" no-gutters>
        <v-card
          flat
          v-for="(vehicle, index) in connectedVehicles"
          :key="'fleet-vehicle-' + index"
          :style="smAndDown ? 'width: 100%' : 'width: 50%'"
          class="py-1 pa-md-1"
          color="transparent"
        >
          <DashboardVehicleListCard :vehicle="vehicle" @click="emitViewVehicle(vehicle)" height="100%" />
        </v-card>
      </v-row>
      <v-row
        v-else-if="searchQuery && unfilteredData.length"
        class="text-body-1 text-grey my-4"
        no-gutters
      >
        No vehicles match your search query "{{ searchQuery }}"
        {{
          filter_onlyActiveConnections || filter_orderByStateOfCharge
            ? 'with the applied filters'
            : ''
        }}.
      </v-row>
      <v-row v-else class="text-body-1 text-grey my-4" no-gutters>
        You have no connected vehicles
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import Vehicle from '@/classes/vehicle_classes/vehicle'
import { GettersTypes, MainDialogContent, MutationTypes } from '@/store/store_types'
import { computed, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import GenericDialog from '../dialog-wrappers/GenericDialog.vue'
import TextBtn from '../ui-elements/buttons/TextBtn.vue'
import DashboardVehicleListCard from './DashboardVehicleListCard.vue'
import DefaultVehicleCard from './DefaultVehicleCard.vue'

const { smAndDown } = useDisplay()
const store = useStore()
const emit = defineEmits<{
  (e: 'view-vehicle', vehicle: Vehicle): void
  (e: 'add-vehicle'): void
}>()

const showFleetFilterDialog = ref(false)
const filter_onlyActiveConnections = ref(false)
const filter_orderByStateOfCharge = ref(false)
const searchQuery = ref('')
const unfilteredData = computed<Vehicle[]>(() => store.getters[GettersTypes.connectedVehicles])
const connectedVehicles = computed<Vehicle[]>(() => {
  console.log('recompute of filtered vehicles triggered')

  let filteredData: Vehicle[] = []
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filteredData = [...unfilteredData.value.filter((vehicle) => {
      if (vehicle.name && vehicle.name.toLowerCase().includes(query)) return vehicle
      if (vehicle.VIN && vehicle.VIN.toLowerCase().includes(query)) return vehicle
      if (vehicle.licensePlate && vehicle.licensePlate.toLowerCase().includes(query)) return vehicle
      if (vehicle.evModel) {
        if (vehicle.evModel.name.toLowerCase().includes(query)) return vehicle
      }
    })]
  } else {
    filteredData = [...unfilteredData.value]
  }
  if (filter_onlyActiveConnections.value) {
    filteredData = filteredData.filter((vehicle) => vehicle.lastOrakleUpdateSuccessful)
  }
  if (filter_orderByStateOfCharge.value) {
    console.log('filter order by SoC inner if');
    
    filteredData = [...filteredData].sort((a, b) => (b.stateOfCharge ?? 0) - (a.stateOfCharge ?? 0))
  }
  return filteredData
})
const customVehicles = computed<Vehicle[]>(() => store.getters[GettersTypes.customVehicles])

/**
 * Emits an event to view a vehicle. If the vehicle being viewed is the
 * same as the selected vehicle, the event emits `undefined` as the vehicle
 * ID. Otherwise, the event emits the local ID of the vehicle.
 *
 * @param {Vehicle} vehicle - The vehicle to be viewed.
 * @return {void} This function does not return anything.
 */
function emitViewVehicle(vehicle: Vehicle) {
  emit('view-vehicle', vehicle)
}

function handleAddVehicle() {
  store.commit(MutationTypes.setMainDialogContent, MainDialogContent.ADD_VEHICLE)
}
</script>
